import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Toaster } from 'sonner';
import Header from './components/Header';
import LightSwitch from './components/LightSwitch';
import CursorToggle from './components/CursorToggle';
import LanguageToggle from './components/LanguageToggle';
import MessageWidget from './components/MessageWidget';
import PuzzleWidget from './components/PuzzleWidget';
import BookingModal from './components/BookingModal';
import WaiverModal, { WaiverFloatingButton, openWaiver } from './components/WaiverModal';
import HHWaiverModal, { openHHWaiver } from './components/HHWaiverModal';
import ImageLightbox from './components/ImageLightbox';
import SloganPopup from './components/SloganPopup';
import HeroSection from './components/HeroSection';
import HalloweenBanner from './components/HalloweenBanner';
import KeyLockPuzzle from './components/KeyLockPuzzle';
import FeatureWidgets from './components/FeatureWidgets';
import HowItWorks from './components/HowItWorks';
import EscapeRooms from './components/EscapeRooms';
import PricingSection from './components/PricingSection';
import LocationsContact from './components/LocationsContact';
import CrosswordPuzzle from './components/CrosswordPuzzle';
import CTABanner from './components/CTABanner';
import Footer from './components/Footer';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import TreasureHunt from './pages/TreasureHunt';
import DirectionsPage from './pages/DirectionsPage';
import FAQPage from './pages/FAQPage';
import UnderConstructionPage from './pages/UnderConstructionPage';
import WeeklyPuzzlePage from './pages/WeeklyPuzzlePage';
import GuardianApprovePage from './pages/GuardianApprovePage';
import ScannerPage from './pages/ScannerPage';
import DoomsdayCountdown from './components/DoomsdayCountdown';

const API = process.env.REACT_APP_BACKEND_URL;

function ProtectedRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="min-h-screen bg-black flex items-center justify-center"><span className="font-heading text-brand text-xl">Loading...</span></div>;
  if (!user) return <Navigate to="/admin/login" />;
  return children;
}

function LandingPage() {
  const { isDark } = useTheme();
  const { search } = useLocation();

  // QR / deep-link entry: /?book=haunted (or /haunted route) auto-opens the Haunted House ticket flow
  useEffect(() => {
    const params = new URLSearchParams(search);
    const book = params.get('book');
    const waiver = params.get('waiver');
    const order = params.get('order');
    if (book === 'haunted' || book === 'halloween') {
      const t = setTimeout(() => {
        import('./components/BookingModal').then(m => m.openBookingModal('halloween-disclaimer'));
      }, 350);
      return () => clearTimeout(t);
    }
    // /?waiver=haunted or /?waiver=haunted&order=<hold_id> auto-opens the HH waiver
    if (waiver === 'haunted') {
      const t = setTimeout(() => {
        if (order) {
          // Pre-fill from the order so buyer name/email land in the form
          import('axios').then(({ default: ax }) => {
            ax.get(`${API}/api/hh/waiver/order/${encodeURIComponent(order)}`)
              .then(r => openHHWaiver({
                hold_id: r.data?.hold_id,
                order_id: r.data?.order_id,
                order_num: r.data?.order_num,
                buyer_name: r.data?.buyer_name,
                buyer_email: r.data?.buyer_email,
                buyer_phone: r.data?.buyer_phone,
              }))
              .catch(() => openHHWaiver({}));
          });
        } else {
          openHHWaiver({});
        }
      }, 300);
      return () => clearTimeout(t);
    }
    // /?waiver=1 → open the escape-room waiver (used by the "Forward Link" button)
    if (waiver === '1' || waiver === 'escape') {
      const res = params.get('res');
      const t = setTimeout(() => openWaiver(res ? { reservation_number: res } : null), 300);
      return () => clearTimeout(t);
    }
  }, [search]);

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      <Header />
      <div className="hidden sm:block fixed right-4 top-36 z-40">
        <LightSwitch />
        <CursorToggle />
      </div>
      <LanguageToggle className="fixed top-20 sm:top-36 left-2 sm:left-4 z-50" />
      <AnimatePresence>
        {!isDark && (
          <motion.div
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.3 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            className="fixed left-14 sm:left-4 top-20 sm:top-48 z-40"
          >
            <Link
              to="/treasure"
              data-testid="treasure-lightbulb"
              title="Follow the light — find the treasure"
              className="relative flex flex-col items-center gap-1 hover:scale-110 transition-transform cursor-pointer"
            >
              <Lightbulb size={40} className="text-yellow-400 drop-shadow-[0_0_12px_rgba(250,204,21,0.8)]" fill="rgba(250,204,21,0.3)" />
              <span className="text-xs font-heading tracking-wider text-yellow-400 uppercase">ON</span>
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
      <main>
        <HalloweenBanner />
        <HeroSection />
        <KeyLockPuzzle />
        <EscapeRooms />
        <FeatureWidgets />
        <HowItWorks />
        <PricingSection />
        <LocationsContact />
        <CrosswordPuzzle />
        <CTABanner />
      </main>
      <Footer />
      <MessageWidget />
      <PuzzleWidget />
      <WaiverFloatingButton />
      <BookingModal />
      <WaiverModal />
      <HHWaiverModal />
      <ImageLightbox />
      <SloganPopup />
    </div>
  );
}

function AppRoutes() {
  const [underConstruction, setUnderConstruction] = useState(false);
  const [checked, setChecked] = useState(false);
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');

  useEffect(() => {
    axios.get(`${API}/api/settings/site`)
      .then(res => { setUnderConstruction(res.data.under_construction); setChecked(true); })
      .catch(() => setChecked(true));
    // Track visit (fire-and-forget, unique per IP per day)
    axios.post(`${API}/api/track/visit`).catch(() => {});
  }, []);

  if (!checked) return null;

  // Under construction blocks public routes but admin routes stay accessible
  if (underConstruction) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/scan" element={<ScannerPage />} />
        <Route path="/haunted" element={<Navigate to="/?book=haunted" replace />} />
        <Route path="/haunted/waiver" element={<HauntedWaiverRedirect />} />
        <Route path="/waiver/approve/:token" element={<GuardianApprovePage />} />
        <Route path="*" element={<UnderConstructionPage />} />
      </Routes>
    );
  }

  return (
    <>
      {!isAdmin && <DoomsdayCountdown />}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/haunted" element={<Navigate to="/?book=haunted" replace />} />
        <Route path="/haunted/waiver" element={<HauntedWaiverRedirect />} />
        <Route path="/waiver/approve/:token" element={<GuardianApprovePage />} />
        <Route path="/directions" element={<DirectionsPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/puzzle" element={<WeeklyPuzzlePage />} />
        <Route path="/treasure" element={<TreasureHunt />} />
        <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/scan" element={<ScannerPage />} />
    </Routes>
    </>
  );
}

// Small helper: /haunted/waiver?order=<hold_id> → normalized to /?waiver=haunted&order=<...>
function HauntedWaiverRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const order = params.get('order');
  const to = order ? `/?waiver=haunted&order=${encodeURIComponent(order)}` : '/?waiver=haunted';
  return <Navigate to={to} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <ThemeProvider>
            <AppRoutes />
            <Toaster theme="dark" richColors position="top-center" />
          </ThemeProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
